import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "./firebase/firebaseConfig";
import { onAuthStateChanged, getIdTokenResult, signOut } from "firebase/auth";
import {
 collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
  deleteDoc,
} from "firebase/firestore";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [claims, setClaims] = useState(null);
    const [company, setCompany] = useState(null);
    const [companyId, setCompanyId] = useState(null);  // Added companyId state
    const [platformRole, setPlatformRole] = useState(null); // NEW
    const [role, setRole] = useState(null);                 // optional (company role)

    const [loading, setLoading] = useState(true); // ✅ ADD THIS

    useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
    if (!firebaseUser) {
      setUser(null);
      setClaims(null);
      setCompany(null);
      setCompanyId(null);
      setPlatformRole(null);
      setRole(null);
      setLoading(false);
      return;
    }

    try {
      // 1) Get fresh claims (you already had this)
      const tokenResult = await getIdTokenResult(firebaseUser);
      const userClaims = tokenResult.claims;
      setPlatformRole(userClaims?.platformRole ?? null);
      setRole(userClaims?.role ?? null);

      // 2) Always look up by UID
      const userRef = doc(db, "users", firebaseUser.uid);
      let snap = await getDoc(userRef);

      // 3) One-time migration: if old doc exists keyed by email (or random id), move it to /users/{uid}
      if (!snap.exists()) {
        const byEmailQ = query(
          collection(db, "users"),
          where("email", "==", firebaseUser.email)
        );
        const byEmailQS = await getDocs(byEmailQ);

        if (!byEmailQS.empty) {
          const oldDoc = byEmailQS.docs[0];
          // merge old data into the canonical /users/{uid} doc
          await setDoc(
            userRef,
            { uid: firebaseUser.uid, ...oldDoc.data() },
            { merge: true }
          );
          // tidy up (best-effort)
          try {
            await deleteDoc(doc(db, "users", oldDoc.id));
          } catch {}
          snap = await getDoc(userRef);
        }
      }

     // 4) During /signup the profile is created AFTER we claim/validate license.
      //    Don't sign out while signup is in progress.
   if (!snap.exists()) {
   const onSignupRoute =
     typeof window !== "undefined" &&
     window.location.pathname.toLowerCase().includes("/signup");
   if (!onSignupRoute) {
     console.warn("No provisioned profile found. Signing out.");
     await signOut(auth);
   } else {
     console.info("On /signup: skipping auto sign-out until profile is created.");
   }
   setLoading(false);
   return;
 }

      // 5) Proceed with your existing logic
      const userData = snap.data();
      setUser(userData);
      setClaims(userClaims);

      const userCompanyId = userData.companyId;
      setCompanyId(userCompanyId);

      if (userCompanyId) {
        const companyDocRef = doc(db, "companies", userCompanyId);
        const companyDocSnap = await getDoc(companyDocRef);

        if (companyDocSnap.exists()) {
          setCompany(companyDocSnap.data());
        } else {
          console.warn("No company document found for companyId:", userCompanyId);
          setCompany(null);
        }
      } else {
        console.warn("User profile has no companyId.");
        setCompany(null);
      }
    } catch (error) {
      console.error("Error fetching user/company data:", error);
      setUser(null);
      setCompany(null);
    } finally {
      setLoading(false);
    }
  });

  return () => unsubscribe();
}, []);

 
    const refreshClaims = async () => {
       // Force a fresh ID token so newly-set custom claims (e.g., platformRole) appear immediately
       await auth.currentUser?.getIdToken(true);
     };

     return (
         <AuthContext.Provider value={{
             user,
             claims,
             platformRole, // NEW — use this directly in your App header
             role,         // optional
             company,
             companyId,
             loading,
             refreshClaims
         }}>
             {children}
         </AuthContext.Provider>
     );
 };
 
 export const useAuth = () => {
     return useContext(AuthContext);
 };
